import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getAllUpcomingWebinars,
  getPastWebinarsPaginated,
} from '@domain/features/webinars/webinars-feature'
import { getSyncedPastWebinars } from '@domain/features/webinars/webinars-youtube-sync-feature'
import type { Webinar } from '@domain/models/webinar'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'

export type WebinarSegment = 'latest' | 'upcoming'
const PAST_WEBINARS_PAGE_SIZE = 10

function mergeWebinarsByUid(existing: Webinar[], incoming: Webinar[]): Webinar[] {
  const byUid = new Map(existing.map((webinar) => [webinar.uid, webinar]))
  for (const webinar of incoming) {
    byUid.set(webinar.uid, webinar)
  }
  return [...byUid.values()]
}

function mergePreferredWebinarsKeepingLimit(
  preferred: Webinar[],
  fallback: Webinar[],
  limit: number,
): Webinar[] {
  const merged: Webinar[] = []
  const seenUids = new Set<string>()

  for (const webinar of preferred) {
    if (seenUids.has(webinar.uid)) {
      continue
    }
    seenUids.add(webinar.uid)
    merged.push(webinar)
  }

  for (const webinar of fallback) {
    if (seenUids.has(webinar.uid)) {
      continue
    }
    seenUids.add(webinar.uid)
    merged.push(webinar)
  }

  return merged.slice(0, Math.max(0, limit))
}

export function useWebinars(
  segment: WebinarSegment,
  webinarCategoryId: string | null = null,
) {
  const ssrData = useSsrPageData()
  const ssrWebinars = segment === 'upcoming'
    ? ssrData?.webinarsUpcoming?.webinars
    : ssrData?.webinarsLatest?.webinars
  const ssrHasMore = segment === 'upcoming'
    ? false
    : ssrData?.webinarsLatest?.hasMore
  const { user, loading: authLoading } = useAuthUser()
  const [webinars, setWebinars] = useState<Webinar[]>(ssrWebinars ?? [])
  const [loading, setLoading] = useState(!ssrWebinars)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [youtubeHasMore, setYoutubeHasMore] = useState(false)
  const [firestoreHasMore, setFirestoreHasMore] = useState(ssrHasMore ?? true)
  const [firestoreCursor, setFirestoreCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [offset, setOffset] = useState(0)
  const normalizedCategoryId = webinarCategoryId?.trim() ?? ''
  const isCategoryFilterActive = normalizedCategoryId.length > 0
  const shouldAutoSync = user?.isAdmin === true
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('list')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    enabled: !authLoading,
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && webinars.length === 0

      try {
        if (showInitialLoading) {
          setLoading(true)
        }
        setRefreshing(false)
        setError(null)

        if (segment === 'upcoming') {
          const upcoming = await getAllUpcomingWebinars(
            normalizedCategoryId || undefined,
          )
          if (requestId !== requestIdRef.current) {
            return
          }
          setWebinars(upcoming)
          setYoutubeHasMore(false)
          setFirestoreHasMore(false)
          setFirestoreCursor(null)
          setOffset(0)
          return
        }

        if (isCategoryFilterActive) {
          const firstPage = await getPastWebinarsPaginated(
            PAST_WEBINARS_PAGE_SIZE,
            null,
            normalizedCategoryId || undefined,
          )
          if (requestId !== requestIdRef.current) {
            return
          }
          setWebinars(firstPage.webinars)
          setYoutubeHasMore(false)
          setFirestoreHasMore(firstPage.hasMore)
          setFirestoreCursor(firstPage.lastDoc)
          setOffset(0)
          return
        }

        const firstPage = await getPastWebinarsPaginated(PAST_WEBINARS_PAGE_SIZE)
        if (requestId !== requestIdRef.current) {
          return
        }

        setWebinars(firstPage.webinars)
        setYoutubeHasMore(false)
        setFirestoreHasMore(firstPage.hasMore)
        setFirestoreCursor(firstPage.lastDoc)
        setOffset(PAST_WEBINARS_PAGE_SIZE)
        setRefreshing(shouldAutoSync)

        if (!shouldAutoSync) {
          return
        }

        void (async () => {
          try {
            const latestResult = await getSyncedPastWebinars({
              count: PAST_WEBINARS_PAGE_SIZE,
              offset: 0,
            })
            if (requestId !== requestIdRef.current) {
              return
            }

            const mergedFirstPageWebinars = mergePreferredWebinarsKeepingLimit(
              latestResult.webinars,
              firstPage.webinars,
              PAST_WEBINARS_PAGE_SIZE,
            )
            if (mergedFirstPageWebinars.length > 0 || firstPage.webinars.length === 0) {
              setWebinars(mergedFirstPageWebinars)
            }
            setYoutubeHasMore(latestResult.hasMore)
            setOffset(PAST_WEBINARS_PAGE_SIZE)
          } catch {
            // Keep Firestore list visible when sync is unavailable.
          } finally {
            if (requestId === requestIdRef.current) {
              setRefreshing(false)
            }
          }
        })()
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinars.',
          )
        }
        throw loadError
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    if (authLoading) {
      return
    }

    void triggerRefresh('mount', { force: true })
  }, [
    segment,
    authLoading,
    user?.uid,
    user?.isAdmin,
    shouldAutoSync,
    isCategoryFilterActive,
    normalizedCategoryId,
    triggerRefresh,
  ])

  const loadMore = useCallback(async () => {
    if (
      segment !== 'latest' ||
      loading ||
      loadingMore ||
      (!youtubeHasMore && !firestoreHasMore)
    ) {
      return
    }

    try {
      setLoadingMore(true)
      setError(null)

      if (isCategoryFilterActive) {
        const filteredPage = await getPastWebinarsPaginated(
          PAST_WEBINARS_PAGE_SIZE,
          firestoreCursor,
          normalizedCategoryId || undefined,
        )
        setWebinars((previous) => mergeWebinarsByUid(previous, filteredPage.webinars))
        setFirestoreCursor(filteredPage.lastDoc)
        setFirestoreHasMore(filteredPage.hasMore)
        return
      }

      if (shouldAutoSync && youtubeHasMore) {
        const nextPage = await getSyncedPastWebinars({
          count: PAST_WEBINARS_PAGE_SIZE,
          offset,
        })
        setWebinars((previous) => mergeWebinarsByUid(previous, nextPage.webinars))
        setYoutubeHasMore(nextPage.hasMore)
        setOffset((previous) => previous + PAST_WEBINARS_PAGE_SIZE)
        return
      }

      const fallbackPage = await getPastWebinarsPaginated(
        PAST_WEBINARS_PAGE_SIZE,
        firestoreCursor,
      )
      setWebinars((previous) => mergeWebinarsByUid(previous, fallbackPage.webinars))
      setFirestoreCursor(fallbackPage.lastDoc)
      setFirestoreHasMore(fallbackPage.hasMore)
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load more webinars.',
      )
    } finally {
      setLoadingMore(false)
    }
  }, [
    segment,
    isCategoryFilterActive,
    loading,
    loadingMore,
    youtubeHasMore,
    firestoreHasMore,
    shouldAutoSync,
    offset,
    firestoreCursor,
    normalizedCategoryId,
  ])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  const hasMore = segment === 'latest'
    && (
      isCategoryFilterActive
        ? firestoreHasMore
        : (firestoreHasMore || (shouldAutoSync && youtubeHasMore))
    )

  return {
    webinars,
    loading,
    refreshing,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
